/* eslint-disable react-refresh/only-export-components -- route builder plus its layout components. */
import { CssBaseline } from "@mui/material";
import { Outlet, type RouteObject } from "react-router-dom";
import { AuthProtectedRoute } from "src/infrastructure/auth";
import { AppLayout } from "src/components/layout/AppLayout";
import { SessionProvider } from "src/features/session";
import type { ModuleDescriptor } from "src/app/moduleDescriptor";
import { ModuleGuard } from "src/app/ModuleGuard";
import { buildNavItems, useGrantedEntitlements } from "src/app/moduleAccess";
import { AppErrorPage } from "src/pages/AppErrorPage/AppErrorPage.page";
import { NotFoundPage } from "src/pages/NotFoundPage/NotFoundPage.page";
import { SignInCallbackPage } from "src/pages/SignInCallbackPage/SignInCallbackPage.page";

function RootLayout() {
  return (
    <>
      <CssBaseline />
      <Outlet />
    </>
  );
}

// The drawer shows only the modules the user may open; built here, in the
// shell, so shared layout components don't reach into the session.
function ShellLayout({ moduleList }: { moduleList: readonly ModuleDescriptor[] }) {
  const entitlements = useGrantedEntitlements();
  return <AppLayout navItems={buildNavItems(moduleList, entitlements)} />;
}

// Module descriptors hold absolute paths, which the drawer links to as-is;
// data-router children need paths relative to their parent, and the root path
// "/" must be an index route rather than a child with path: "/".
// Every module page goes through ModuleGuard, so a gated route is blocked even
// when reached by a typed or bookmarked URL rather than the drawer.
function toChildRoute(module: ModuleDescriptor): RouteObject {
  const element = <ModuleGuard module={module}>{module.element}</ModuleGuard>;
  if (module.path === "/") {
    return { index: true, element };
  }
  return { path: module.path.slice(1), element };
}

/**
 * Route table from the module registry, plus the Auth0 callback route and the
 * not-found and error fallbacks.
 * Reference: twosion-ui src/App.tsx.
 */
export function buildAppRoutes(moduleList: readonly ModuleDescriptor[]): RouteObject[] {
  return [
    {
      element: <RootLayout />,
      children: [
        {
          // Under RootLayout, so the error page keeps CssBaseline.
          errorElement: <AppErrorPage />,
          children: [
            {
              path: "/",
              element: (
                <AuthProtectedRoute>
                  <SessionProvider>
                    <ShellLayout moduleList={moduleList} />
                  </SessionProvider>
                </AuthProtectedRoute>
              ),
              children: moduleList.map(toChildRoute),
            },
            {
              // Auth0's return address when AUTH0_CALLBACK_URL ends in /callback.
              // Not behind AuthProtectedRoute: Auth0 is still finishing sign-in here.
              path: "/callback",
              element: <SignInCallbackPage />,
            },
            {
              path: "*",
              element: (
                <AuthProtectedRoute>
                  <NotFoundPage />
                </AuthProtectedRoute>
              ),
            },
          ],
        },
      ],
    },
  ];
}
